import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronRight, Coins, Info, Shield } from 'lucide-react';
import { getMoneta, isValidMoneta, MONETE_SLUGS, MONETE_ORO } from '@/lib/moneteOroData';
import { useStore } from '@/store/useStore';
import { FALLBACK_METAL_PRICES } from '@/server/config/ssr-config';

interface Props { ssrMetalPrices?: any; }

export default function MonetaOroSingola({ ssrMetalPrices }: Props) {
  const { moneta } = useParams<{ moneta: string }>();
  const { currentGoldPrice } = useStore();

  if (!moneta || !isValidMoneta(moneta)) {
    return <Navigate to="/monete-oro-investimento" replace />;
  }

  const m = getMoneta(moneta)!;
  const goldPrice24K =
    currentGoldPrice?.price ?? ssrMetalPrices?.gold?.price ?? FALLBACK_METAL_PRICES.gold.price;

  const valoreOroFino = goldPrice24K * m.pesoOroFinoG;
  const premiumMedio = 0.05;
  const valoreMercato = valoreOroFino * (1 + premiumMedio);

  const fmtEuro = (v: number) =>
    `${Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`;
  const fmtPrice = (v: number) => v.toFixed(2).replace('.', ',');

  const canonical = `https://www.gildy.it/monete-oro/${m.slug}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.gildy.it/' },
          { '@type': 'ListItem', position: 2, name: 'Monete oro investimento', item: 'https://www.gildy.it/monete-oro-investimento' },
          { '@type': 'ListItem', position: 3, name: m.nome, item: canonical },
        ],
      },
      {
        '@type': 'Product',
        name: m.nome,
        description: m.intro,
        category: 'Moneta da investimento in oro',
        countryOfOrigin: m.paese,
        weight: { '@type': 'QuantitativeValue', value: m.pesoTotaleG, unitCode: 'GRM' },
        material: `Oro ${m.titolo} (${m.caratura})`,
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'EUR',
          lowPrice: Math.round(valoreOroFino),
          highPrice: Math.round(valoreMercato * 1.05),
          availability: 'https://schema.org/InStock',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: m.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{m.titleTag}</title>
        <meta name="description" content={m.metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta property="og:title" content={m.titleTag} />
        <meta property="og:description" content={m.metaDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="product" />
        <meta property="og:locale" content="it_IT" />
        <meta property="og:site_name" content="Gildy" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <nav className="flex items-center text-sm text-muted-foreground mb-4" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-4 w-4 mx-1" />
          <Link to="/monete-oro-investimento" className="hover:text-foreground">Monete oro</Link>
          <ChevronRight className="h-4 w-4 mx-1" />
          <span className="text-foreground">{m.nome}</span>
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{m.h1}</h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8">{m.intro}</p>

        <Card className="mb-8 border-gold/30 bg-gradient-to-br from-gold/5 to-transparent">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Coins className="h-5 w-5 text-gold" />
              Quotazione indicativa oggi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-muted-foreground">Peso totale:</span> <strong>{m.pesoTotaleG}g</strong></div>
              <div><span className="text-muted-foreground">Oro fino:</span> <strong>{m.pesoOroFinoG.toFixed(2)}g</strong></div>
              <div><span className="text-muted-foreground">Titolo:</span> <strong>{m.titolo}</strong></div>
              <div><span className="text-muted-foreground">Diametro:</span> <strong>{m.diametroMm} mm</strong></div>
            </div>
            <div className="border-t border-gold/20 pt-3 space-y-1">
              <p className="text-sm">
                Spot oro 24kt: <strong>{fmtPrice(goldPrice24K)} €/g</strong> ·
                Valore oro fino: <strong>{fmtEuro(valoreOroFino)}</strong>
              </p>
              <p className="text-base">
                Valore di mercato indicativo (premium ~5%):{' '}
                <strong className="text-gold text-lg">{fmtEuro(valoreMercato)}</strong>
              </p>
              <p className="text-xs text-muted-foreground">
                Quotazione basata sul prezzo spot LBMA + premium medio. Annate rare e stato di conservazione possono modificare il valore reale.
              </p>
            </div>
          </CardContent>
        </Card>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Info className="h-5 w-5 text-metal-primary" />
            Come riconoscere un {m.nome} autentico
          </h2>
          <ul className="space-y-2">
            {m.comeRiconoscerla.map((bullet, idx) => (
              <li key={idx} className="flex gap-3 text-muted-foreground leading-relaxed">
                <span className="text-gold font-bold">·</span>
                <span dangerouslySetInnerHTML={{ __html: bullet.replace(/'/g, '&apos;') }} />
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-metal-primary" />
            Premium di mercato
          </h2>
          <p className="text-muted-foreground leading-relaxed">{m.premiumInfo}</p>
        </section>

        {m.faqs.length > 0 && (
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-4">Domande frequenti</h2>
            <div className="space-y-4">
              {m.faqs.map((f, idx) => (
                <Card key={idx}>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-foreground mb-2">{f.q}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{f.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12 pt-8 border-t">
          <h2 className="text-xl font-bold text-foreground mb-4">Altre monete d'oro da investimento</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-6">
            {MONETE_SLUGS.filter((s) => s !== m.slug).map((s) => (
              <Link key={s} to={`/monete-oro/${s}`} className="text-sm text-metal-primary hover:underline">
                {MONETE_ORO[s].nome}
              </Link>
            ))}
          </div>
          <Card className="bg-gradient-to-r from-gold/10 to-transparent border-gold/30">
            <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5">
              <p className="text-sm">
                <strong>Vuoi vendere una moneta?</strong> Confronta i compro oro con spread trasparente e perizia gratuita.
              </p>
              <Link to="/compro-oro-vicino-a-me" className="text-sm font-medium text-gold hover:text-gold-light whitespace-nowrap">
                Trova compro oro vicino a te →
              </Link>
            </CardContent>
          </Card>
        </section>
      </div>
    </>
  );
}
